import { useCallback, useEffect, useState } from 'react'
import { Download, FileText, ShieldAlert } from 'lucide-react'
import { copyImage } from '@/lib/copyImage'
import { formatSize } from '@/lib/upload'
import type { Attachment } from '@/types/api'
import { Modal } from '../settings/Modal'
import { isMac } from './useFormatToolbar'
import styles from './AttachmentView.module.css'

const MAX_W = 400
const MAX_H = 300

/** Вложения сообщения: картинки с лайтбоксом, видео и аудио с плеером, остальное — карточкой. */
export function AttachmentView({ attachments }: { attachments: Attachment[] }) {
  const [open, setOpen] = useState<Attachment | null>(null)
  if (attachments.length === 0) return null
  return (
    <div className={styles.list}>
      {attachments.map((a) => {
        const src = a.proxy_url || a.url
        if (a.blocked) {
          return (
            <div key={a.id} className={styles.blocked} role="note">
              <ShieldAlert size={28} className={styles.blockedIcon} />
              <span className={styles.fileText}>
                <span className={styles.fileName}>{a.filename}</span>
                <span className={styles.fileSize}>Файл удалён: антивирус нашёл угрозу</span>
              </span>
            </div>
          )
        }
        if (a.content_type.startsWith('image/')) {
          const size = fit(a.width ?? 0, a.height ?? 0)
          return (
            <button key={a.id} type="button" className={styles.imageButton} onClick={() => setOpen(a)} aria-label={`Открыть ${a.filename}`}>
              <img src={src} alt={a.description ?? a.filename} loading="lazy" className={styles.image} style={size} />
            </button>
          )
        }
        if (a.content_type.startsWith('video/')) {
          return <video key={a.id} src={src} controls preload="metadata" className={styles.video} data-tooltip={a.filename} aria-label={a.filename} />
        }
        if (a.content_type.startsWith('audio/')) {
          return (
            <div key={a.id} className={styles.audio}>
              <span className={styles.audioName}>{a.filename}</span>
              <audio src={src} controls preload="metadata" />
            </div>
          )
        }
        return (
          <a key={a.id} href={a.url} className={styles.file} target="_blank" rel="noreferrer noopener" download={a.filename}>
            <FileText size={28} className={styles.fileIcon} />
            <span className={styles.fileText}>
              <span className={styles.fileName}>{a.filename}</span>
              <span className={styles.fileSize}>{formatSize(a.size)}</span>
            </span>
            <Download size={18} className={styles.fileDownload} />
          </a>
        )
      })}
      {open ? <Lightbox image={open} onClose={() => setOpen(null)} /> : null}
    </div>
  )
}

/**
 * Картинка на весь экран. Ctrl+C (на маке ⌘C) копирует саму картинку, как в Telegram, — если
 * в окне не выделен текст; кнопка «Копировать» — то же для мыши. Клавишу ловим по коду, а не по
 * букве: на русской раскладке «C» — это «С».
 */
function Lightbox({ image, onClose }: { image: Attachment; onClose: () => void }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')

  const copy = useCallback(() => {
    copyImage(image.url)
      .then(() => setState('copied'))
      .catch((err: unknown) => {
        setState('failed')
        if (err instanceof Error) console.warn('картинка не скопировалась:', err.message)
      })
  }, [image.url])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== 'KeyC' || e.altKey || e.shiftKey || !(isMac ? e.metaKey : e.ctrlKey)) return
      if (window.getSelection()?.toString()) return
      // отменённое нажатие не превращается в команду «копировать» — второго раза не будет
      e.preventDefault()
      copy()
    }
    // «Копировать» из меню (в приложении для компьютера ⌘C может прийти через меню, а не клавишей)
    const onCopy = (e: ClipboardEvent) => {
      if (window.getSelection()?.toString()) return
      e.preventDefault()
      copy()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('copy', onCopy)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('copy', onCopy)
    }
  }, [copy])

  useEffect(() => {
    if (state === 'idle') return
    const t = window.setTimeout(() => setState('idle'), 2000)
    return () => window.clearTimeout(t)
  }, [state])

  return (
    <Modal title={image.filename} onClose={onClose} width={Math.min(window.innerWidth - 48, Math.max(480, (image.width ?? 800) + 32))}>
      <img src={image.url} alt={image.description ?? image.filename} className={styles.lightbox} />
      <div className={styles.lightboxBar}>
        <a href={image.url} className={styles.lightboxLink} target="_blank" rel="noreferrer noopener">Открыть оригинал · {formatSize(image.size)}</a>
        <button type="button" className={styles.lightboxLink} data-tooltip={isMac ? '⌘C' : 'Ctrl+C'} onClick={copy} aria-live="polite">
          {state === 'copied' ? 'Скопировано' : state === 'failed' ? 'Не удалось скопировать' : 'Копировать'}
        </button>
      </div>
    </Modal>
  )
}

function fit(w: number, h: number): { width: number; aspectRatio: string } | undefined {
  if (!w || !h) return undefined
  const k = Math.min(1, MAX_W / w, MAX_H / h)
  return { width: Math.round(w * k), aspectRatio: `${w} / ${h}` }
}
